import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, Shield, BarChart3, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useConsent } from '@/lib/consent';
import { cn } from '@/lib/utils';

interface ConsentBannerProps {
  className?: string;
}

export function ConsentBanner({ className }: ConsentBannerProps) {
  const { consent, hasUserConsented, acceptAll, rejectAll, updateConsent } = useConsent();
  const [showDetails, setShowDetails] = useState(false);

  // Don't show banner if user has already consented
  if (hasUserConsented) {
    return null;
  }

  const handleCustomConsent = () => {
    updateConsent({
      analytics: consent.analytics,
      marketing: consent.marketing,
      functional: true, // Always true
    });
    setShowDetails(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className={cn(
          'fixed bottom-0 left-0 right-0 z-50 p-3 md:p-4 bg-background/95 backdrop-blur-sm border-t border-gray-800',
          className
        )}
      >
        <div className="container mx-auto max-w-6xl">
          <AnimatePresence mode="wait">
            {!showDetails ? (
              <motion.div
                key="banner"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-4"
              >
                <div className="flex items-start gap-2 md:gap-3 flex-1">
                  <div className="w-6 h-6 md:w-8 md:h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Shield className="h-3 w-3 md:h-4 md:w-4 text-indigo-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-xs md:text-sm mb-1">We value your privacy</h3>
                    <p className="text-gray-400 text-xs md:text-sm leading-relaxed hidden md:block">
                      We use cookies to enhance your experience and analyze site traffic. 
                      Essential cookies are always active. Choose your preferences for analytics and marketing cookies.
                    </p>
                    <p className="text-gray-400 text-xs leading-relaxed md:hidden">
                      We use cookies to enhance your experience. Essential cookies are always active.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 w-full md:w-auto">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDetails(true)}
                    className="text-gray-400 hover:text-white px-2 md:px-3 flex-1 md:flex-none"
                  >
                    <Settings className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                    <span className="hidden sm:inline">Customize</span>
                    <span className="sm:hidden">Options</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={rejectAll}
                    className="border-gray-700 hover:bg-gray-800 px-2 md:px-3 text-xs md:text-sm flex-1 md:flex-none"
                  >
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    onClick={acceptAll}
                    className="bg-indigo-600 hover:bg-indigo-700 px-2 md:px-3 text-xs md:text-sm flex-1 md:flex-none"
                  >
                    Accept
                  </Button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="details"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Shield className="h-4 w-4 text-indigo-500" />
                    Cookie Preferences
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDetails(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
                  {/* Essential Cookies */}
                  <Card className="bg-gray-900/50 border-gray-700">
                    <CardHeader className="pb-2 md:pb-3">
                      <CardTitle className="text-xs md:text-sm flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Shield className="h-3 w-3 md:h-4 md:w-4 text-green-500" />
                          Essential
                        </span>
                        <Switch checked={true} disabled />
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-xs text-gray-400">
                        Required for basic site functionality, security, and remembering your preferences.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Analytics Cookies */}
                  <Card className="bg-gray-900/50 border-gray-700">
                    <CardHeader className="pb-2 md:pb-3">
                      <CardTitle className="text-xs md:text-sm flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <BarChart3 className="h-3 w-3 md:h-4 md:w-4 text-blue-500" />
                          Analytics
                        </span>
                        <Switch
                          checked={consent.analytics}
                          onCheckedChange={(checked) =>
                            updateConsent({ ...consent, analytics: checked })
                          }
                        />
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-xs text-gray-400">
                        Help us understand how visitors interact with our website to improve user experience.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Marketing Cookies */}
                  <Card className="bg-gray-900/50 border-gray-700">
                    <CardHeader className="pb-2 md:pb-3">
                      <CardTitle className="text-xs md:text-sm flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Target className="h-3 w-3 md:h-4 md:w-4 text-pink-500" />
                          Marketing
                        </span>
                        <Switch
                          checked={consent.marketing}
                          onCheckedChange={(checked) =>
                            updateConsent({ ...consent, marketing: checked })
                          }
                        />
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-xs text-gray-400">
                        Used to deliver relevant advertisements and measure campaign effectiveness.
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div className="flex flex-col md:flex-row items-start md:items-center justify-between pt-2 gap-3 md:gap-0">
                  <div className="text-xs text-gray-500 order-2 md:order-1">
                    Learn more in our{' '}
                    <a href="/privacy-policy" className="text-indigo-400 hover:text-indigo-300 underline">
                      Privacy Policy
                    </a>
                  </div>
                  <div className="flex items-center gap-2 w-full md:w-auto order-1 md:order-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDetails(false)}
                      className="border-gray-700 hover:bg-gray-800 px-2 md:px-3 text-xs md:text-sm flex-1 md:flex-none"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleCustomConsent}
                      className="bg-indigo-600 hover:bg-indigo-700 px-2 md:px-3 text-xs md:text-sm flex-1 md:flex-none"
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}